import React, { useRef } from "react";
import { IDropdownOption } from "@fluentui/react";

type SubProduct = {
    manual_sub_key: string;
    faq_sub_key: string;
    text: string;
};

type ProductVersion = {
    manual_version_key: string;
    faq_version_key: string;
    text: string;
    sub_versions?: SubProduct[];
};

type Product = {
    manual_key: string;
    faq_key: string;
    text: string;
    isSelectable?: string;
    is_sub_only?: string;
    versions?: any[];
};

interface ProductFilterDropdownProps {
    products: Product[];
    productText: string;
    productVersionValue: string;
    subProductValue: string;
    isHovered: number;
    isSubHovered: number;
    isVersionsHovered: number;
    setIsHovered: (value: number) => void;
    setIsSubHovered: (value: number) => void;
    setIsVersionsHovered: (value: number) => void;
    handleProductClick: (item: Product) => void;
    handleVersionChange: (option: IDropdownOption | undefined, product: Product) => void;
    handleSubProductChange: (option: IDropdownOption | undefined, product: Product, version: ProductVersion) => void;
    handleTopPosition: (divReference: any, index: number, itemsLength: number) => number | string;
    renderNested?: boolean;
    renderProductItem?: (item: Product, index: number) => React.ReactNode;
    onProductMouseEnter?: (item: Product, index: number) => void;
    productContainerClassName?: string;
    getProductItemClassName?: (item: Product, index: number) => string;
    renderProductLabel?: (item: Product, index: number) => React.ReactNode;
    productItemRef?: (el: HTMLDivElement | null, index: number) => void;
}

export const ProductFilterDropdown: React.FC<ProductFilterDropdownProps> = ({
    products,
    productText,
    productVersionValue,
    subProductValue,
    isHovered,
    isSubHovered,
    isVersionsHovered,
    setIsHovered,
    setIsSubHovered,
    setIsVersionsHovered,
    handleProductClick,
    handleVersionChange,
    handleSubProductChange,
    handleTopPosition,
    renderNested = true,
    renderProductItem,
    onProductMouseEnter,
    productContainerClassName,
    getProductItemClassName,
    renderProductLabel,
    productItemRef
}) => {
    const versionDropdownRefs = useRef<(HTMLDivElement | null)[]>([]);
    const subVersionDropdownRefs = useRef<(HTMLDivElement | null)[]>([]);
    const onKeyActivate = (event: React.KeyboardEvent, action: () => void) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            action();
        }
    };

    if (renderProductItem) {
        return <>{products.map((item, index) => renderProductItem(item, index))}</>;
    }

    const renderProductRow = (item: Product, index: number) => (
        <div
            key={index}
            ref={el => {
                productItemRef?.(el, index);
            }}
            onMouseEnter={() => {
                setIsHovered(index);
                onProductMouseEnter?.(item, index);
            }}
            onMouseLeave={() => {
                // In toolbar integration mode (renderNested=false), keep legacy hover behavior
                // and let the parent manage hover transitions.
                if (!renderNested) return;
                setIsHovered(-1);
                setIsSubHovered(-1);
            }}
            onClick={() => handleProductClick(item)}
            onKeyDown={event => onKeyActivate(event, () => handleProductClick(item))}
            role="button"
            tabIndex={0}
            title={item?.text?.toString()}
            className={getProductItemClassName?.(item, index)}
            style={
                getProductItemClassName
                    ? undefined
                    : {
                          cursor: "pointer",
                          padding: "8px 12px",
                          backgroundColor: productText === item.text ? "#e6f4ff" : "transparent"
                      }
            }
        >
            {renderProductLabel ? renderProductLabel(item, index) : item.text}
        </div>
    );

    return (
        <>
            {products.map((item, index) => {
                if (!renderNested) {
                    return renderProductRow(item, index);
                }

                return (
                    <div key={index} className={productContainerClassName} style={{ position: "relative" }}>
                        {renderProductRow(item, index)}
                        {isHovered === index && item.versions && item.versions.length > 0 && (
                            <div
                                ref={el => {
                                    versionDropdownRefs.current[index] = el;
                                }}
                                style={{
                                    position: "absolute",
                                    top: handleTopPosition(versionDropdownRefs, index, item.versions.length),
                                    left: "100%",
                                    backgroundColor: "white",
                                    border: "1px solid #ccc",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                    zIndex: 1000,
                                    minWidth: "200px"
                                }}
                            >
                                {item.versions.map((version, vIndex) => (
                                    <div key={vIndex} style={{ position: "relative" }}>
                                        <div
                                            onMouseEnter={() => setIsVersionsHovered(vIndex)}
                                            onMouseLeave={() => setIsVersionsHovered(-1)}
                                            onClick={() =>
                                                handleVersionChange(
                                                    {
                                                        key: version.manual_version_key,
                                                        text: version.text
                                                    },
                                                    item
                                                )
                                            }
                                            onKeyDown={event =>
                                                onKeyActivate(event, () =>
                                                    handleVersionChange(
                                                        {
                                                            key: version.manual_version_key,
                                                            text: version.text
                                                        },
                                                        item
                                                    )
                                                )
                                            }
                                            role="button"
                                            tabIndex={0}
                                            style={{
                                                cursor: "pointer",
                                                padding: "8px 12px",
                                                backgroundColor: productVersionValue === version.text ? "#e6f4ff" : "transparent"
                                            }}
                                        >
                                            {version.text}
                                        </div>

                                        {isVersionsHovered === vIndex && version.sub_versions && version.sub_versions.length > 0 && (
                                            <div
                                                ref={el => {
                                                    subVersionDropdownRefs.current[vIndex] = el;
                                                }}
                                                style={{
                                                    position: "absolute",
                                                    top: handleTopPosition(subVersionDropdownRefs, vIndex, version.sub_versions.length),
                                                    left: "100%",
                                                    backgroundColor: "white",
                                                    border: "1px solid #ccc",
                                                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                                    zIndex: 1001,
                                                    minWidth: "200px"
                                                }}
                                            >
                                                {version.sub_versions.map((subVersion: SubProduct, sIndex: number) => (
                                                    <div
                                                        key={sIndex}
                                                        onClick={() =>
                                                            handleSubProductChange(
                                                                {
                                                                    key: subVersion.manual_sub_key,
                                                                    text: subVersion.text
                                                                },
                                                                item,
                                                                version
                                                            )
                                                        }
                                                        onKeyDown={event =>
                                                            onKeyActivate(event, () =>
                                                                handleSubProductChange(
                                                                    {
                                                                        key: subVersion.manual_sub_key,
                                                                        text: subVersion.text
                                                                    },
                                                                    item,
                                                                    version
                                                                )
                                                            )
                                                        }
                                                        role="button"
                                                        tabIndex={0}
                                                        style={{
                                                            cursor: "pointer",
                                                            padding: "8px 12px",
                                                            backgroundColor: subProductValue === subVersion.text ? "#e6f4ff" : "transparent"
                                                        }}
                                                    >
                                                        {subVersion.text}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </>
    );
};
